import React, { useState, SetStateAction, useEffect } from 'react';
import { useIntl } from 'react-intl';
import * as TFF from '@tff/types/tff';
import { FlightDetails as NDCFlightDetails } from '@tff/types/ndc';
import { AmendBookingRequest } from '@tff/types/tff/amend-booking-service';
import { RecordLocator } from '@tff/types';
import { useRebook } from '../../RebookContext';
import { TypeOfStatus } from '../../../../hooks/use-amend-booking';
import TFFAlert from '../../../../styled-components/TFFAlert';
import TFFTypography from '../../../../styled-components/TFFTypography';
import TFFDialog from '../../../../styled-components/TFFDialog';
import NdcSearchHeader from './NdcSearchHeader';
import NdcSearchFooter from './NdcSearchFooter';
import NdcChangeFlightCard from './NdcChangeFlightCard';

type CabinProduct = 'ECONOMY' | 'BUSINESS';

interface NdcSearchPageProps {
  orderDetails: TFF.OrderDetails;
  isRoundTrip: boolean;
  journeys: TFF.Journey[];
  airports: TFF.Airport[];
  recordLocator: string;
  rebookCloser: (fn: SetStateAction<boolean>) => void;
  midocoOrderNo?: number;
  cancelHandler: () => void;
  retrieveDb?: TFF.Retrieve;
  setAmendBookingRequest: (request: AmendBookingRequest) => void;
  amendStatus?: TypeOfStatus;
  amendError?: Error;
}

const NdcSearchPage: React.FC<NdcSearchPageProps> = ({
  journeys,
  orderDetails,
  airports,
  recordLocator,
  rebookCloser,
  midocoOrderNo,
  cancelHandler,
  retrieveDb,
  setAmendBookingRequest,
  amendStatus,
  amendError,
  isRoundTrip,
}) => {
  const { rebookSearchState } = useRebook();
  const [showWarning, setShowWarning] = useState(false);
  const [searchAlternativeFlights, setSearchAlternativeFlights] = useState(false);
  const [isSearchingForLowerClass, setIsSearchingForLowerClass] = useState(false);
  const [isLowerClass, setIsLowerClass] = useState(false);
  const [outboundCabinClass, setOutboundCabinClass] = useState('');
  const [inboundCabinClass, setInboundCabinClass] = useState('');
  const intl = useIntl();

  const handleRequest = async () => {
    if (isLowerClass) {
      setIsSearchingForLowerClass(true);
    }

    const outboundSearchFields = rebookSearchState['Outbound'][0];
    const [outboundDeparture, outboundOrigin, outboundDestination] = outboundSearchFields;

    const flightDetailsRequest: NDCFlightDetails[] = [
      {
        Origin: outboundOrigin,
        Destination: outboundDestination,
        DepDate: outboundDeparture,
        CabinProduct: outboundSearchFields[outboundSearchFields.length - 1].toUpperCase() as CabinProduct,
      },
    ];

    const journeyOndsRequest = [orderDetails.Journeys[0].Ond];

    if (isRoundTrip) {
      const lastJourney = orderDetails.Journeys[orderDetails.Journeys.length - 1];
      const inboundSearchFields = rebookSearchState['Return'][0] ?? '';
      const [inboundDeparture, inboundOrigin, inboundDestination] = inboundSearchFields;

      flightDetailsRequest.push({
        Origin: inboundOrigin,
        Destination: inboundDestination,
        DepDate: inboundDeparture,
        CabinProduct: inboundSearchFields[inboundSearchFields.length - 1].toUpperCase() as CabinProduct,
      });

      journeyOndsRequest.push(lastJourney.Ond);
    }

    const searchRequest: AmendBookingRequest = {
      action: 'search-alternatives',
      // @ts-ignore
      bookingSource: `TUI-NDC-${retrieveDb?.bookingSource}`,
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: retrieveDb?.tfmPnr!,
      midocoOrderNo: midocoOrderNo,
      flightDetails: flightDetailsRequest,
      journeyONDs: journeyOndsRequest,
    };

    setAmendBookingRequest(searchRequest);
    setSearchAlternativeFlights(true);
  };

  useEffect(() => {
    if (searchAlternativeFlights && amendStatus !== 'IN_VALIDATING' && amendStatus) {
      setShowWarning(amendStatus === 'FAILURE');
    }
  }, [amendStatus]);

  useEffect(() => {
    setInboundCabinClass(outboundCabinClass);
  }, [outboundCabinClass]);

  useEffect(() => {
    setOutboundCabinClass(inboundCabinClass);
  }, [inboundCabinClass]);

  return (
    <div>
      {showWarning && <TFFAlert variant="warning" text={`${amendError}`} extraStyles={{ width: '100%' }} />}
      <TFFTypography text="Rebook" variant="lead" size="large" fontWeight="bold" modifier="uppercase" />
      {journeys.map((j, index) => {
        const isFirstJourney = index === 0;
        return (
          <React.Fragment key={j.JourneyIdentifier}>
            <NdcSearchHeader isFirstJourney={isFirstJourney} />
            <NdcChangeFlightCard
              journey={j}
              airports={airports}
              orderDetails={orderDetails}
              setLowerClass={setIsLowerClass}
              setSelectedCabinClass={index === 0 ? setOutboundCabinClass : setInboundCabinClass}
              selectedCabinClass={index === 0 ? outboundCabinClass : inboundCabinClass}
            />
          </React.Fragment>
        );
      })}
      <NdcSearchFooter handleRequest={handleRequest} rebookCloser={rebookCloser} cancelHandler={cancelHandler} />
      <TFFDialog
        dialogId="lowerClassWarning"
        header={<TFFTypography text="Amend notification" fontWeight="bold" variant="lead" size="large" />}
        isOpen={isSearchingForLowerClass}
        closeButton
        onCancel={() => setIsSearchingForLowerClass(false)}
        closeButtonText="Close"
        extraStyle={{ zIndex: 10000 }}
      >
        <TFFAlert variant="warning" text={intl.formatMessage({ id: 'rebook.CabinWarning' })} />
      </TFFDialog>
    </div>
  );
};

export default NdcSearchPage;
